// Client for the print-pro API worker (printpro.xiaomiao.win). Payment itself is
// proxied by that worker to the central billing-service.

const API_BASE = 'https://printpro.xiaomiao.win';

export interface PlanSpec {
  plan: '1m' | '1y';
  label: string;
  price: number;
  /** Strikethrough anchor (competitor price). */
  origPrice: number;
  badge?: string;
}

// origPrice 锚点是竞品价（DocuGenius ¥59.9 / ¥305 / ¥610）。
// 2026-09-11 起只留月付 / 年付两档，各比竞品（¥29 / ¥269）低 ¥10。
// 月付 2026-09-04 起降到 ¥18.8（原 ¥29.9），拉低首次付费门槛。
// **改价必须同步 billing KV product:print-pro 的 plans —— 那边才是实收价。**
// badge 别写死折扣数字：18.8/59.9 ≈ 3.1 折，写「5折」就成了虚假宣传。
export const PLANS: PlanSpec[] = [
  { plan: '1m', label: '月付', price: 19, origPrice: 29, badge: '限时特价' },
  { plan: '1y', label: '年付', price: 259, origPrice: 348, badge: '最划算' },
];

export interface AccessInfo {
  pro: boolean;
  /** 自己没买，靠同企业里付过费的人。 */
  viaTeam?: boolean;
  freeUsed: number;
  freeLimit: number;
  freeRemaining: number;
}

export interface ConsumeResult {
  allowed: boolean;
  pro: boolean;
  freeUsed?: number;
  freeLimit?: number;
  freeRemaining?: number;
}

export interface OrderInfo {
  outTradeNo: string;
  qrCode: string;
  amount: string;
  durationDays: number;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error((data && data.message) || `HTTP ${res.status}`);
  return data as T;
}

import { getTenantKey } from '../api/bitable';

// 企业标识只取一次；拿不到（SDK 不支持）就当没有，权益退回按个人算
let tenantP: Promise<string | null> | null = null;
const tenant = () => (tenantP ??= getTenantKey().catch(() => null));

export const billingApi = {
  async access(accountId: string): Promise<AccessInfo> {
    const t = await tenant();
    return req(`/api/access?accountId=${encodeURIComponent(accountId)}${t ? `&tenant=${encodeURIComponent(t)}` : ''}`);
  },
  async consume(accountId: string): Promise<ConsumeResult> {
    const tenantKey = (await tenant()) || undefined;
    return req('/api/consume', { method: 'POST', body: JSON.stringify({ accountId, tenantKey }) });
  },
  /** 下单：权益归企业（tenantKey），一人买全员用。 */
  async order(accountId: string, plan: string): Promise<OrderInfo> {
    const tenantKey = (await tenant()) || undefined;
    return req('/api/order', { method: 'POST', body: JSON.stringify({ accountId, plan, tenantKey }) });
  },
  queryOrder(outTradeNo: string): Promise<{ paid: boolean }> {
    return req(`/api/order/query?outTradeNo=${encodeURIComponent(outTradeNo)}`);
  },
};
